import React, { useCallback, useState } from 'react';
import { Typography } from 'antd';
import {
  UserOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  InfoCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { clientBookingsApi } from '../../../api/clientBookingsApi';
import { useBookingFlowStore } from '../../../store/bookingFlowStore';
import { useMainButton } from '../../../hooks/useMainButton';
import { useHaptic } from '../../../hooks/useHaptic';
import type { CreateBookingRes } from '../../../types/client';

interface Props {
  onSuccess: (res: CreateBookingRes) => void;
  onConflict: () => void;
}

const LOCALE_MAP: Record<string, string> = {
  uz: 'uz-UZ',
  ru: 'ru-RU',
  en: 'en-US',
};

function formatDate(date: string, locale: string): string {
  return new Date(`${date}T00:00:00`).toLocaleDateString(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}

export const ConfirmationStep: React.FC<Props> = ({ onSuccess, onConflict }) => {
  const { t, i18n } = useTranslation('customer');
  const locale = LOCALE_MAP[i18n.language] ?? 'uz-UZ';
  const providerId = useBookingFlowStore((s) => s.providerId);
  const providerDisplayName = useBookingFlowStore((s) => s.providerDisplayName);
  const serviceId = useBookingFlowStore((s) => s.serviceId);
  const selectedDate = useBookingFlowStore((s) => s.selectedDate);
  const selectedTime = useBookingFlowStore((s) => s.selectedTime);
  const confirmationModel = useBookingFlowStore((s) => s.confirmationModel);
  const isSubmitting = useBookingFlowStore((s) => s.isSubmitting);
  const submitError = useBookingFlowStore((s) => s.submitError);
  const setSubmitting = useBookingFlowStore((s) => s.setSubmitting);
  const setSubmitError = useBookingFlowStore((s) => s.setSubmitError);

  const [notes, setNotes] = useState('');
  const haptic = useHaptic();

  const handleSubmit = useCallback(async () => {
    if (
      providerId === null ||
      serviceId === null ||
      selectedDate === null ||
      selectedTime === null ||
      isSubmitting
    )
      return;

    setSubmitting(true);
    setSubmitError(null);

    try {
      const res = await clientBookingsApi.create({
        providerId,
        serviceId,
        date: selectedDate,
        time: selectedTime,
        customerNotes: notes.trim() !== '' ? notes.trim() : undefined,
      });
      haptic.notifySuccess();
      onSuccess(res);
    } catch (err: unknown) {
      const status =
        err !== null &&
        typeof err === 'object' &&
        'response' in err &&
        err.response !== null &&
        typeof err.response === 'object' &&
        'status' in err.response
          ? (err.response as { status: unknown }).status
          : undefined;

      const errCode = (err as { response?: { data?: { code?: string } } })?.response?.data?.code;
      const errMessage = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;

      if (status === 409) {
        haptic.notifyError();
        onConflict();
      } else if (status === 429 && errCode === 'BOOKING_COOLDOWN_ACTIVE') {
        haptic.notifyError();
        setSubmitError(errMessage ?? t('bookingFlow.confirmation.cooldownError'));
      } else {
        haptic.notifyError();
        setSubmitError(t('bookingFlow.confirmation.submitError'));
      }
    } finally {
      setSubmitting(false);
    }
  }, [
    providerId,
    serviceId,
    selectedDate,
    selectedTime,
    notes,
    isSubmitting,
    setSubmitting,
    setSubmitError,
    haptic,
    onSuccess,
    onConflict,
    t,
  ]);

  useMainButton(t('bookingFlow.confirmation.bookBtn'), () => void handleSubmit(), !isSubmitting);

  return (
    <div>
      {/* Title */}
      <div style={{ padding: '14px 16px 4px' }}>
        <Typography.Text style={{ fontSize: 17, fontWeight: 700, color: 'var(--bl-text)', letterSpacing: -0.3 }}>
          {t('bookingFlow.confirmation.title')}
        </Typography.Text>
      </div>

      {/* Error card */}
      {submitError !== null && (
        <div style={{
          margin: '8px 16px',
          borderRadius: 'var(--bl-radius)',
          background: 'var(--bl-surface)',
          border: '1px solid rgba(239,68,68,0.25)',
          padding: '10px 14px',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <div style={{
            width: 32, height: 32, borderRadius: 'var(--bl-radius-sm)', flexShrink: 0,
            background: 'rgba(239,68,68,0.10)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 15, color: 'rgba(239,68,68,1)',
          }}>
            <CloseCircleOutlined />
          </div>
          <Typography.Text style={{ fontSize: 13, fontWeight: 500, color: 'rgba(220,38,38,1)', flex: 1 }}>
            {submitError}
          </Typography.Text>
        </div>
      )}

      {/* Booking summary card */}
      <div style={{
        margin: '8px 16px',
        borderRadius: 'var(--bl-radius-lg)',
        background: 'var(--bl-surface)',
        border: '1px solid var(--bl-divider)',
        overflow: 'hidden',
      }}>
        {/* Provider row */}
        {providerId !== null && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '12px 14px',
            borderBottom: '1px solid var(--bl-divider)',
          }}>
            <div style={{
              width: 36, height: 36, borderRadius: 'var(--bl-radius-sm)', flexShrink: 0,
              background: 'rgba(51,144,236,0.10)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 16, fontWeight: 600,
              color: 'var(--bl-primary)',
            }}>
              {providerDisplayName?.[0]?.toUpperCase() ?? <UserOutlined />}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Typography.Text style={{ display: 'block', fontSize: 11, color: 'var(--bl-text-secondary)', fontWeight: 500, marginBottom: 1 }}>
                {t('bookingFlow.confirmation.providerLabel')}
              </Typography.Text>
              <Typography.Text style={{ fontSize: 15, fontWeight: 600, color: 'var(--bl-text)' }}>
                {providerDisplayName ?? '—'}
              </Typography.Text>
            </div>
          </div>
        )}

        {/* Date row */}
        {selectedDate !== null && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '12px 14px',
            borderBottom: selectedTime !== null ? '1px solid var(--bl-divider)' : undefined,
          }}>
            <div style={{
              width: 36, height: 36, borderRadius: 'var(--bl-radius-sm)', flexShrink: 0,
              background: 'rgba(31,139,76,0.10)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 15, color: 'rgba(31,139,76,1)',
            }}>
              <CalendarOutlined />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Typography.Text style={{ display: 'block', fontSize: 11, color: 'var(--bl-text-secondary)', fontWeight: 500, marginBottom: 1 }}>
                {t('bookingFlow.confirmation.dateLabel')}
              </Typography.Text>
              <Typography.Text style={{ fontSize: 15, fontWeight: 600, color: 'var(--bl-text)' }}>
                {formatDate(selectedDate, locale)}
              </Typography.Text>
            </div>
          </div>
        )}

        {/* Time row */}
        {selectedTime !== null && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '12px 14px',
          }}>
            <div style={{
              width: 36, height: 36, borderRadius: 'var(--bl-radius-sm)', flexShrink: 0,
              background: 'rgba(217,119,6,0.10)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 15, color: 'rgba(217,119,6,1)',
            }}>
              <ClockCircleOutlined />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Typography.Text style={{ display: 'block', fontSize: 11, color: 'var(--bl-text-secondary)', fontWeight: 500, marginBottom: 1 }}>
                {t('bookingFlow.confirmation.timeLabel')}
              </Typography.Text>
              <Typography.Text style={{ fontSize: 15, fontWeight: 600, color: 'var(--bl-text)' }}>
                {selectedTime}
              </Typography.Text>
            </div>
          </div>
        )}

      </div>

      {/* Confirmation model notice */}
      {confirmationModel !== null && (
        <div style={{
          margin: '8px 16px',
          borderRadius: 'var(--bl-radius)',
          background: 'rgba(51,144,236,0.10)',
          border: '1px solid var(--bl-divider)',
          padding: '10px 14px',
          display: 'flex', alignItems: 'flex-start', gap: 10,
        }}>
          <div style={{
            width: 28, height: 28, borderRadius: 'var(--bl-radius-sm)', flexShrink: 0,
            background: 'var(--bl-primary)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 13, color: '#fff', marginTop: 1,
          }}>
            <InfoCircleOutlined />
          </div>
          <Typography.Text style={{ fontSize: 13, color: 'var(--bl-primary)', fontWeight: 500, flex: 1 }}>
            {confirmationModel === 'INSTANT'
              ? t('bookingFlow.confirmation.noticeInstant')
              : t('bookingFlow.confirmation.noticeApproval')}
          </Typography.Text>
        </div>
      )}

      {/* Notes */}
      <div style={{ margin: '8px 16px 16px' }}>
        <Typography.Text style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--bl-text-secondary)', marginBottom: 8 }}>
          {t('bookingFlow.confirmation.notesLabel')}
        </Typography.Text>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={500}
          rows={3}
          placeholder={t('bookingFlow.confirmation.notesPlaceholder')}
          disabled={isSubmitting}
          style={{
            width: '100%',
            borderRadius: 'var(--bl-radius)',
            border: '1px solid var(--bl-divider)',
            background: 'var(--bl-surface)',
            padding: '10px 12px',
            fontSize: 15,
            color: 'var(--bl-text)',
            fontFamily: 'inherit',
            resize: 'none',
            outline: 'none',
            boxSizing: 'border-box',
            opacity: isSubmitting ? 0.6 : 1,
          }}
        />
        <Typography.Text style={{ display: 'block', fontSize: 11, color: 'var(--bl-text-secondary)', textAlign: 'right', marginTop: 4 }}>
          {t('bookingFlow.confirmation.notesCounter', { current: notes.length, max: 500 })}
        </Typography.Text>
      </div>
    </div>
  );
};
